import React from 'react';
import {
  Box, Typography, Stack, TextField,
  FormControlLabel, Radio, RadioGroup,
} from '@mui/material';
import { iconOptions } from '../../../business/NitzavSpotlightWidget/iconOptions';
import { PARNAS_TYPES, PARNAS_TYPE_DEFAULT_ICONS } from '../../../business/ParnasWidget/parnasConstants';

const navy    = '#1e3a5f';
const inputSx = { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' } };

/**
 * ParnasTypeStep
 * בחירת סוג הפרנסות ואייקון כללי לרכיב.
 *
 * Props:
 *   formData  { parnasType, parnasTypeCustomLabel, parnasTypeIcon }
 *   onChange  (partial) => void
 */
const ParnasTypeStep = ({ formData, onChange }) => {
  const parnasType            = formData?.parnasType            || 'maor';
  const parnasTypeCustomLabel = formData?.parnasTypeCustomLabel || '';
  const parnasTypeIcon        = formData?.parnasTypeIcon        || PARNAS_TYPE_DEFAULT_ICONS[parnasType] || 'star';

  const update = (partial) => onChange(partial);

  const handleTypeChange = (value) => {
    update({
      parnasType:     value,
      parnasTypeIcon: PARNAS_TYPE_DEFAULT_ICONS[value] || 'star',
      // אפס label אם חוזר מ-acher לסוג אחר
      ...(value !== 'acher' ? { parnasTypeCustomLabel: '' } : {}),
    });
  };

  return (
    <Box dir="rtl">
      <Typography sx={{ fontWeight: 700, color: navy, fontSize: '1rem', mb: 0.5 }}>
        סוג הפרנסות
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', mb: 2.5, lineHeight: 1.5 }}>
        בחר לאיזו פרנסות שייך הרכיב הזה. הסוג יוצג בכותרת ומשפיע על האייקון הברירת מחדל.
      </Typography>

      {/* סוג פרנסות */}
      <Box sx={{ bgcolor: 'white', border: '1px solid #e5e7eb', borderRadius: 2, p: 2.5, mb: 2 }}>
        <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.875rem', mb: 1.5 }}>
          סוג הפרנסות
        </Typography>
        <RadioGroup
          value={parnasType}
          onChange={(e) => handleTypeChange(e.target.value)}
        >
          <Stack spacing={0.5}>
            {PARNAS_TYPES.map((t) => (
              <FormControlLabel
                key={t.value}
                value={t.value}
                control={<Radio size="small" sx={{ color: '#9ca3af', '&.Mui-checked': { color: navy } }} />}
                label={
                  <Typography sx={{ fontSize: '0.875rem', color: '#374151' }}>
                    {t.label}
                  </Typography>
                }
                sx={{ m: 0, py: 0.25 }}
              />
            ))}
          </Stack>
        </RadioGroup>

        {/* שם חופשי כשבוחרים "אחר" */}
        {parnasType === 'acher' && (
          <TextField
            size="small"
            label="שם הפרנסות"
            placeholder="תיאור הפרנסות..."
            value={parnasTypeCustomLabel}
            onChange={(e) => update({ parnasTypeCustomLabel: e.target.value })}
            fullWidth
            sx={{ ...inputSx, mt: 1.5 }}
            helperText="שם זה יוצג בכותרת הרכיב"
          />
        )}
      </Box>

      {/* בחירת אייקון כללי */}
      <Box sx={{ bgcolor: 'white', border: '1px solid #e5e7eb', borderRadius: 2, p: 2.5 }}>
        <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.875rem', mb: 0.5 }}>
          אייקון כללי לרכיב
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', color: '#6b7280', mb: 1.5 }}>
          האייקון שיוצג בכותרת הרכיב. ניתן לשנות את ברירת המחדל.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {iconOptions.map(({ id, label, icon: IconComponent }) => {
            const isSelected = parnasTypeIcon === id;
            return (
              <Box
                key={id}
                onClick={() => update({ parnasTypeIcon: id })}
                sx={{
                  width: 54,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '3px',
                  py: 0.75,
                  px: 0.5,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  border: isSelected ? '2px solid #111827' : '1px solid #e5e7eb',
                  bgcolor: isSelected ? '#f9fafb' : 'white',
                  boxShadow: isSelected ? '0 0 0 3px rgba(17,24,39,0.06)' : 'none',
                  '&:hover': {
                    borderColor: isSelected ? '#111827' : '#9ca3af',
                    bgcolor: isSelected ? '#f9fafb' : '#f8fafc',
                  },
                }}
              >
                <IconComponent sx={{ fontSize: 20, color: isSelected ? '#111827' : '#6b7280' }} />
                <Typography sx={{
                  fontSize: '0.6rem',
                  color: isSelected ? '#111827' : '#9ca3af',
                  fontWeight: isSelected ? 600 : 400,
                  lineHeight: 1,
                  textAlign: 'center',
                }}>
                  {label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default ParnasTypeStep;
